import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MountainsIcon, ForkKnifeIcon, MagnifyingGlassIcon, LeafIcon, PhoneIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const API = process.env.REACT_APP_BACKEND_URL;

// Guest-facing digital menu — no auth required. Rendered from a QR scan.
export default function PublicMenu() {
  const { pid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/public/menu/${pid}`)
      .then((r) => { setData(r.data); setActiveCat(r.data.categories?.[0]?.name || ""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Menu unavailable"));
  }, [pid]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return (data.categories || [])
      .map((c) => ({ ...c, items: c.items.filter((i) => !q || i.name.toLowerCase().includes(q) || (i.notes || "").toLowerCase().includes(q)) }))
      .filter((c) => c.items.length > 0);
  }, [data, query]);

  if (err) {
    return (
      <div className="min-h-screen bg-primary text-primary-foreground flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <ForkKnifeIcon size={40} className="mx-auto text-gold mb-4" weight="duotone" />
          <h1 className="font-display text-2xl">Menu unavailable</h1>
          <p className="text-sm text-primary-foreground/70 mt-2">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading menu…</div>;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Heritage header */}
      <div className="relative bg-primary text-primary-foreground overflow-hidden">
        <img
          src="https://images.pexels.com/photos/36827299/pexels-photo-36827299.jpeg?auto=compress&cs=tinysrgb&h=500&w=1200"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-primary/85 to-primary" />
        <div className="relative z-10 p-6 pt-10 pb-8 max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-primary-foreground/70 text-xs uppercase tracking-widest">
            <MountainsIcon size={14} weight="duotone" /> {data.brand.group_name}
          </div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight mt-2 text-gold" data-testid="menu-hotel-name">{data.property.name}</h1>
          {data.property.address && <p className="text-sm text-primary-foreground/80 mt-1">{data.property.address}, {data.property.city}</p>}
          {data.property.phone && <p className="text-xs text-primary-foreground/70 mt-2 flex items-center gap-1"><PhoneIcon size={12} /> {data.property.phone}</p>}
          <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest text-gold border-t border-gold/30 pt-4">
            <ForkKnifeIcon size={14} weight="duotone" /> Restaurant &amp; Bar
          </div>
        </div>
      </div>

      {/* Sticky search + category pills */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="relative">
            <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search dish, drink…"
              className="w-full rounded-full border border-border pl-9 pr-4 py-2 text-sm bg-card"
              data-testid="menu-search"
            />
          </div>
          {filtered.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pt-3 pb-1 -mx-1 px-1" data-testid="menu-category-pills">
              {filtered.map((c) => (
                <a
                  key={c.name}
                  href={`#cat-${c.name}`}
                  onClick={() => setActiveCat(c.name)}
                  className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border shrink-0 ${activeCat === c.name ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-border"}`}
                >
                  {c.name}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category-wise · international style — sticky top nav with anchor
          links, gold section rules, dotted leader between item and price. */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-10">
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <ForkKnifeIcon size={40} className="mx-auto text-muted-foreground/40 mb-3" weight="duotone" />
            <p className="text-sm text-muted-foreground">{query ? "No dishes match your search." : "Menu is being prepared. Please check with staff."}</p>
          </div>
        )}
        {filtered.map((cat) => (
          <section key={cat.name} id={`cat-${cat.name}`} data-testid={`menu-cat-${cat.name}`} className="scroll-mt-32">
            <div className="text-center mb-4">
              <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">◆ {cat.items.length} dish{cat.items.length === 1 ? "" : "es"}</p>
              <h2 className="font-display text-2xl mt-1 text-primary">{cat.name}</h2>
              <div className="mx-auto mt-2 h-px w-16 bg-gold" />
            </div>
            <ul className="space-y-3">
              {cat.items.map((item, idx) => (
                <li key={`${cat.name}-${idx}`} className="flex items-baseline gap-2" data-testid={`menu-item-${cat.name}-${idx}`}>
                  <div className="min-w-0">
                    <p className="font-medium leading-tight">{item.name}</p>
                    {item.notes && <p className="text-xs text-muted-foreground mt-0.5 italic">{item.notes}</p>}
                    {item.gst_rate > 0 && <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-0.5">GST {item.gst_rate}%</p>}
                  </div>
                  <span className="flex-1 mx-2 border-b border-dotted border-border/70 translate-y-[-4px]" />
                  <span className="font-display text-lg text-primary whitespace-nowrap" data-testid={`menu-price-${cat.name}-${idx}`}>{inr(item.price)}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="text-center text-[11px] text-muted-foreground pt-6 pb-8 flex items-center justify-center gap-1">
          <LeafIcon size={12} weight="duotone" /> Prices in INR · GST as applicable · Please place your order with our staff or on your room phone.
        </p>
      </div>
    </div>
  );
}
